/**
 * pyRun · живой редактор Python (Skulpt) — пиши код, запускай, смотри вывод.
 * Per ADR-013. Только для блоков ОСНОВ Python (print/переменные/операторы/if/for/
 * списки/словари/функции). Playwright-тесты в браузере не исполнимы — для них code-along.
 *
 * Контракт разметки (авторский · no-JS degradable — без скрипта виден стартовый код):
 *   <div class="py-run" [data-expected="точный stdout для мягкой сверки"]>
 *     <pre class="pyr-code"><code>стартовый код</code></pre>
 *   </div>
 * Движок строит: редактор (textarea со стартовым кодом) + «Запустить» / «Сбросить» +
 * панель вывода (терминал-стиль). Skulpt грузится лениво с cdnjs при первом «Запустить»
 * (single-flight, один раз на приложение). execLimit (~8с) защищает от бесконечных циклов.
 * StrictMode-safe: init-guard + cleanup восстанавливает исходный `.pyr-code`.
 */

import { highlight } from './codeFormat';

/** Источники Skulpt по приоритету — пробуем по очереди (фолбэк, если CDN/версия недоступны). */
const SKULPT_SOURCES: ReadonlyArray<{ main: string; stdlib: string }> = [
  {
    main: 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt.min.js',
    stdlib: 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt-stdlib.js',
  },
  {
    main: 'https://cdnjs.cloudflare.com/ajax/libs/skulpt/1.2.0/skulpt.min.js',
    stdlib: 'https://cdnjs.cloudflare.com/ajax/libs/skulpt/1.2.0/skulpt-stdlib.js',
  },
];

interface SkRuntime {
  configure(opts: Record<string, unknown>): void;
  importMainWithBody(name: string, dump: boolean, body: string, canSuspend: boolean): unknown;
  misceval: { asyncToPromise(fn: () => unknown): Promise<unknown> };
  builtinFiles?: { files: Record<string, string> };
}
const getSk = (): SkRuntime | undefined => (window as unknown as { Sk?: SkRuntime }).Sk;

let skulptPromise: Promise<void> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const prev = document.querySelector<HTMLScriptElement>(`script[data-pyr="${src}"]`);
    if (prev) {
      if (prev.dataset.loaded === '1') {
        resolve();
      } else {
        prev.addEventListener('load', () => resolve());
        prev.addEventListener('error', () => reject(new Error(src)));
      }
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.dataset.pyr = src;
    s.addEventListener('load', () => {
      s.dataset.loaded = '1';
      resolve();
    });
    s.addEventListener('error', () => {
      s.remove(); // дать шанс другому источнику
      reject(new Error(src));
    });
    document.head.appendChild(s);
  });
}

function loadSkulpt(): Promise<void> {
  if (skulptPromise) return skulptPromise;
  // Пробуем источники по очереди: если main или stdlib не загрузились — следующий источник.
  skulptPromise = SKULPT_SOURCES.reduce<Promise<void>>(
    (chain, src) => chain.catch(() => loadScript(src.main).then(() => loadScript(src.stdlib))),
    Promise.reject(new Error('init')),
  )
    .then(() => {
      if (!getSk()) throw new Error('Skulpt загрузился, но недоступен');
    })
    .catch((e: unknown) => {
      skulptPromise = null; // позволить повторную попытку при следующем Run
      throw new Error(
        'Не удалось загрузить Python-движок (проверь интернет). ' +
          (e instanceof Error ? e.message : String(e)),
      );
    });
  return skulptPromise;
}

export function initPyRun(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>('.py-run').forEach((el) => {
    if (el.dataset.pyrInit === '1') return;
    el.dataset.pyrInit = '1';
    const original = el.innerHTML;

    const starter = (el.querySelector('.pyr-code code')?.textContent ?? '').replace(/\s+$/, '');
    const expected = el.dataset.expected ?? '';

    el.innerHTML = '';
    el.classList.add('pyr-ready');

    const bar = document.createElement('div');
    bar.className = 'pye-bar';
    const lang = document.createElement('span');
    lang.className = 'pye-lang';
    lang.textContent = 'PYTHON · редактор';
    const runBtn = document.createElement('button');
    runBtn.type = 'button';
    runBtn.className = 'pye-run';
    runBtn.textContent = 'Запустить';
    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.className = 'pye-reset';
    resetBtn.textContent = 'Сбросить';
    bar.append(lang, runBtn, resetBtn);

    // Поверхность редактора: подсвеченный <pre> позади + прозрачный textarea поверх.
    // Видно цветной код (наши --code-* токены), каретка и ввод — у textarea.
    const wrap = document.createElement('div');
    wrap.className = 'pye-wrap';

    const hl = document.createElement('pre');
    hl.className = 'pye-hl';
    hl.setAttribute('aria-hidden', 'true');

    const ta = document.createElement('textarea');
    ta.className = 'pye-code';
    ta.value = starter;
    ta.spellcheck = false;
    ta.autocapitalize = 'off';
    ta.setAttribute('autocorrect', 'off');
    ta.setAttribute('aria-label', 'Редактор кода Python — пиши и запускай');
    ta.rows = Math.min(Math.max(starter.split('\n').length + 1, 4), 20);

    wrap.append(hl, ta);

    const out = document.createElement('pre');
    out.className = 'pye-out';
    out.setAttribute('aria-live', 'polite');

    el.append(bar, wrap, out);

    // Перекрашиваем подсветку под текущий текст (та же бьютификация, что в код-карточках).
    const render = () => {
      hl.innerHTML = highlight(ta.value, 'python') + '\n';
    };
    const syncScroll = () => {
      hl.scrollTop = ta.scrollTop;
      hl.scrollLeft = ta.scrollLeft;
    };
    render();

    const onInput = () => render();
    const onScroll = () => syncScroll();
    ta.addEventListener('input', onInput);
    ta.addEventListener('scroll', onScroll);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      const s = ta.selectionStart;
      const en = ta.selectionEnd;
      ta.value = ta.value.slice(0, s) + '    ' + ta.value.slice(en);
      ta.selectionStart = ta.selectionEnd = s + 4;
      render();
    };
    ta.addEventListener('keydown', onKey);

    const showOut = (text: string, cls: '' | 'err' | 'ok') => {
      out.textContent = text;
      out.classList.remove('err', 'ok');
      out.classList.add('show');
      if (cls) out.classList.add(cls);
    };

    let running = false;
    const onRun = () => {
      if (running) return;
      running = true;
      runBtn.disabled = true;
      showOut('Запускаю…', '');
      loadSkulpt()
        .then(() => {
          const Sk = getSk();
          if (!Sk) throw new Error('Skulpt не доступен');
          let buf = '';
          Sk.configure({
            output: (t: string) => {
              buf += t;
            },
            read: (x: string) => {
              const f = Sk.builtinFiles?.files[x];
              if (f === undefined) throw new Error("File not found: '" + x + "'");
              return f;
            },
            execLimit: 8000,
          });
          return Sk.misceval
            .asyncToPromise(() => Sk.importMainWithBody('<stdin>', false, ta.value, true))
            .then(() => {
              const clean = buf.replace(/\s+$/, '');
              const result = clean || '(нет вывода — добавь print(...))';
              const matched = expected && clean === expected.replace(/\s+$/, '');
              showOut(matched ? result + '\n\nсовпало с ожидаемым' : result, matched ? 'ok' : '');
            });
        })
        .catch((err: unknown) => {
          showOut(err instanceof Error ? err.message : String(err), 'err');
        })
        .finally(() => {
          running = false;
          runBtn.disabled = false;
        });
    };
    const onReset = () => {
      ta.value = starter;
      render();
      out.textContent = '';
      out.classList.remove('show', 'err', 'ok');
    };
    runBtn.addEventListener('click', onRun);
    resetBtn.addEventListener('click', onReset);

    cleanups.push(() => {
      ta.removeEventListener('keydown', onKey);
      ta.removeEventListener('input', onInput);
      ta.removeEventListener('scroll', onScroll);
      runBtn.removeEventListener('click', onRun);
      resetBtn.removeEventListener('click', onReset);
      el.innerHTML = original;
      el.classList.remove('pyr-ready');
      delete el.dataset.pyrInit;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
